/**
 * WebSocket API Service
 */
import { AuthConfirmationResponse } from '../types/auth.response';
import { logger } from '../utils/logger';
import { eventCentral } from './eventCentral';
import socketService from './socketService';
import type { InvitationRecord, RiskAssessmentAnswer, RiskAssessmentRecord, RiskLevel } from './wsApi.types';

// Re-export types for backward compatibility
export type { InvitationRecord, RiskAssessmentAnswer, RiskAssessmentRecord, RiskLevel } from './wsApi.types';

/**
 * WebSocket API helper functions - Auth only
 */
export const wsApi = {
  /**
   * Send Auth message with JWT token
   * 
   * Request structure (AuthRequest):
   * {
   *   messageId: "...",
   *   messageType: "Auth",
   *   messageTimestamp: "...",
   *   Auth: { jwt: "..." }
   * }
   * 
   * Response (AuthConfirmationResponse):
   * {
   *   messageId: "...",
   *   messageIdReference: "...",
   *   messageTimestamp: "...",
   *   Status: { status: "OK", ... },
   *   messageType: "AuthConfirmation",
   *   AuthConfirmation: { nameLabelDisplay: "...", orgLabelDisplay: "..." }
   * }
   */
  async auth(jwt: string): Promise<AuthConfirmationResponse> {
    logger.debug('[wsApi] Sending Auth request');
    const socket = socketService.getSocket();
    if (!socket) {
      throw new Error('Socket not connected');
    }

    // Build the Auth request payload (just the Auth data, not the full message)
    // The eventCentral.sendRequest will wrap this with messageId, messageType, messageTimestamp
    const authPayload = { jwt };

    // Pass socketService instance to eventCentral which will construct the full AuthRequest
    const response = await eventCentral.sendRequest(socketService, 'Auth', authPayload);

    logger.info('[wsApi] Auth response received', {
      status: response.Status?.status,
      nameLabelDisplay: response.AuthConfirmation?.nameLabelDisplay,
    });

    // Map ResponseMessage to AuthConfirmationResponse
    const mapped: AuthConfirmationResponse = {
      messageId: response.messageId,
      messageIdReference: response.messageIdReference ?? '',
      messageTimestamp: response.messageTimestamp ?? response.timestamp ?? new Date().toISOString(),
      messageType: 'AuthConfirmation',
      Status: response.Status,
      AuthConfirmation: response.AuthConfirmation,
    };

    return mapped;
  },

  // ─── Risk Assessment ────────────────────────────────────────────────────────

  /**
   * Fetch all risk assessments created by this C1 org.
   */
  async riskAssessmentGetAll(): Promise<RiskAssessmentRecord[]> {
    logger.debug('[wsApi] RiskAssessment GET_ALL');
    const socket = socketService.getSocket();
    if (!socket) throw new Error('Socket not connected');
    const response = await eventCentral.sendRequest(socketService, 'RiskAssessment', { action: 'GET_ALL' });
    const assessments = response.RiskAssessmentResponse?.assessments ?? [];
    // GET_ALL is a summary response and normally omits answers (AL-350).
    // Parse legacy/detail-shaped rows only when the field is actually present.
    return assessments.map((a: any) => {
      if (!("answers" in a)) return a;
      return {
        ...a,
        answers: typeof a.answers === 'string' ? JSON.parse(a.answers || '[]') : (a.answers ?? []),
      };
    });
  },

  /**
   * Fetch the latest risk assessment for a specific C2 org.
   */
  async riskAssessmentGet(c2OrganizationId: string): Promise<RiskAssessmentRecord | null> {
    logger.debug('[wsApi] RiskAssessment GET', { c2OrganizationId });
    const socket = socketService.getSocket();
    if (!socket) throw new Error('Socket not connected');
    const response = await eventCentral.sendRequest(socketService, 'RiskAssessment', { action: 'GET', c2OrganizationId });
    const data = response.RiskAssessmentResponse;
    // Backend returns the assessment object directly (or empty {} if not found)
    return data?.assessmentId ? data : null;
  },

  /**
   * Create a new risk assessment for a C2 org.
   */
  async riskAssessmentCreate(
    c2OrganizationId: string,
    riskLevel: RiskLevel,
    answers: RiskAssessmentAnswer[],
    meta?: Partial<Pick<RiskAssessmentRecord, 'clientName' | 'clientRef' | 'assessorName' | 'entityCountry' | 'entityAbn' | 'contactPerson' | 'contactPhone' | 'profession' | 'notes' | 'progress' | 'c2EntityType'>>,
  ): Promise<RiskAssessmentRecord> {
    logger.debug('[wsApi] RiskAssessment CREATE', { c2OrganizationId, riskLevel });
    const socket = socketService.getSocket();
    if (!socket) throw new Error('Socket not connected');
    const { clientName, clientRef, assessorName, entityCountry, entityAbn, contactPerson, contactPhone, profession, notes, progress, c2EntityType } = meta ?? {};
    const response = await eventCentral.sendRequest(socketService, 'RiskAssessment', {
      action: 'CREATE',
      c2OrganizationId,
      riskLevel,
      answers,
      clientName,
      clientRef,
      assessorName,
      entityCountry,
      entityAbn,
      contactPerson,
      contactPhone,
      profession,
      notes,
      progress,
      c2EntityType,
    });
    return response.RiskAssessmentResponse?.assessment ?? response.RiskAssessmentResponse;
  },

  /**
   * Update an existing risk assessment.
   */
  async riskAssessmentUpdate(
    assessmentId: string,
    riskLevel: RiskLevel,
    answers: RiskAssessmentAnswer[],
    meta?: Partial<Pick<RiskAssessmentRecord, 'clientName' | 'clientRef' | 'assessorName' | 'entityCountry' | 'entityAbn' | 'contactPerson' | 'contactPhone' | 'profession' | 'notes' | 'progress' | 'c2EntityType'>>,
  ): Promise<RiskAssessmentRecord> {
    logger.debug('[wsApi] RiskAssessment UPDATE', { assessmentId, riskLevel });
    const socket = socketService.getSocket();
    if (!socket) throw new Error('Socket not connected');
    const { clientName, clientRef, assessorName, entityCountry, entityAbn, contactPerson, contactPhone, profession, notes, progress, c2EntityType } = meta ?? {};
    const response = await eventCentral.sendRequest(socketService, 'RiskAssessment', {
      action: 'UPDATE',
      assessmentId,
      riskLevel,
      answers,
      clientName,
      clientRef,
      assessorName,
      entityCountry,
      entityAbn,
      contactPerson,
      contactPhone,
      profession,
      notes,
      progress,
      c2EntityType,
    });
    return response.RiskAssessmentResponse?.assessment ?? response.RiskAssessmentResponse;
  },

  /**
   * Lock a risk assessment as completed (no further edits allowed).
   */
  async riskAssessmentComplete(assessmentId: string): Promise<RiskAssessmentRecord> {
    logger.debug('[wsApi] RiskAssessment COMPLETE', { assessmentId });
    const socket = socketService.getSocket();
    if (!socket) throw new Error('Socket not connected');
    const response = await eventCentral.sendRequest(socketService, 'RiskAssessment', {
      action: 'COMPLETE',
      assessmentId,
    });
    return response.RiskAssessmentResponse?.assessment ?? response.RiskAssessmentResponse;
  },

  // ─── Recharge / Top-Up ──────────────────────────────────────────────────────

  /**
   * Purchase a credit bundle (top-up / recharge).
   *
   * Request payload sent via eventCentral:
   * {
   *   action: 'PURCHASE',
   *   plan: 'STARTER' | 'STANDARD' | 'PROFESSIONAL' | 'ENTERPRISE',
   *   credits: number,
   *   amount: number          // AUD cents-equivalent total
   * }
   *
   * Expected response:
   * {
   *   RechargeResponse: { success: boolean, creditsAdded: number, newTotal: number }
   * }
   */
  async rechargePurchase(
    plan: string,
    credits: number,
    amount: number,
  ): Promise<{ success: boolean; creditsAdded: number; newTotal: number }> {
    logger.debug('[wsApi] Recharge PURCHASE', { plan, credits, amount });
    const socket = socketService.getSocket();
    if (!socket) throw new Error('Socket not connected');
    const response = await eventCentral.sendRequest(socketService, 'Recharge', {
      action: 'PURCHASE',
      plan,
      credits,
      amount,
    });
    return response.RechargeResponse ?? { success: true, creditsAdded: credits, newTotal: 0 };
  },

  // ─── Create Case (C2 User Registration) ────────────────────────────────────

  /**
   * Register a new C2 user via WebSocket CreateCase message.
   */
  async createCase(payload: {
    firstName: string;
    middleNames?: string;
    lastName: string;
    email: string;
    phone: string;
    dateOfBirth?: string;
    enhancedProfile: number;
    workflow?: string;
    legalEntityCodeType?: string;
    legalEntityCodeData?: string;
    c1Identifier?: string;
  }): Promise<{ action: string; loginId?: string; tempPassword?: string; statusText?: string;[key: string]: any }> {
    logger.debug('[wsApi] CreateCase', { email: payload.email });
    const socket = socketService.getSocket();
    if (!socket) throw new Error('Socket not connected');
    const response = await eventCentral.sendRequest(socketService, 'CreateCase', payload);
    return response.CreateCaseResponse ?? response;
  },

  // ─── C2 Invitations ─────────────────────────────────────────────────────────

  /**
   * Fetch all invitations created by this C1 org.
   */
  async invitationGetAll(): Promise<InvitationRecord[]> {
    logger.debug('[wsApi] C2Invitation GET_ALL');
    const socket = socketService.getSocket();
    if (!socket) throw new Error('Socket not connected');
    const response = await eventCentral.sendRequest(socketService, 'C2Invitation', { action: 'GET_ALL' });
    return response.C2InvitationResponse?.invitations ?? [];
  },

  /**
   * Create a new C2 invitation (generates CDD link on backend).
   */
  async invitationCreate(
    entityName: string,
    cddLink: string,
    recipientEmail?: string,
    extras?: {
      recipientPhone?: string;
      c2Username?: string;
      c2TempPassword?: string;
      entityAbn?: string;
      assessmentId?: string;
    },
  ): Promise<InvitationRecord> {
    logger.debug('[wsApi] C2Invitation CREATE', { entityName });
    const socket = socketService.getSocket();
    if (!socket) throw new Error('Socket not connected');
    const response = await eventCentral.sendRequest(socketService, 'C2Invitation', {
      action: 'CREATE',
      entityName,
      cddLink,
      ...(recipientEmail ? { recipientEmail } : {}),
      ...(extras?.recipientPhone ? { recipientPhone: extras.recipientPhone } : {}),
      ...(extras?.c2Username ? { c2Username: extras.c2Username } : {}),
      ...(extras?.c2TempPassword ? { c2TempPassword: extras.c2TempPassword } : {}),
      ...(extras?.entityAbn ? { entityAbn: extras.entityAbn } : {}),
      ...(extras?.assessmentId ? { assessmentId: extras.assessmentId } : {}),
    });
    // Java lambda returns invitation fields directly in C2InvitationResponse (not nested under .invitation)
    const payload = response.C2InvitationResponse;
    return payload?.invitation ?? payload;
  },

  /**
   * Update an existing C2 invitation (regenerate link or update status).
   */
  async invitationUpdate(
    invitationId: string,
    updates: Partial<{ cddLink: string; status: InvitationRecord['status'] }>,
  ): Promise<InvitationRecord> {
    logger.debug('[wsApi] C2Invitation UPDATE', { invitationId });
    const socket = socketService.getSocket();
    if (!socket) throw new Error('Socket not connected');
    const response = await eventCentral.sendRequest(socketService, 'C2Invitation', {
      action: 'UPDATE',
      invitationId,
      ...updates,
    });
    // Java lambda returns invitation fields directly in C2InvitationResponse (not nested under .invitation)
    const payload = response.C2InvitationResponse;
    return payload?.invitation ?? payload;
  },

};

export default wsApi;
